// tslint:disable: max-line-length
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { AccountManagementComponent } from './account-management/account-management.component';
import { HeapService } from './api-client/heap.service';
import { AppRoutingModule } from './app-routing.module';
import { AuthModule } from './auth/auth.module';
import { OAuthConsentComponent } from './auth/oauth-consent/oauth-consent.component';
import { MainPageComponent } from './main-page/main-page.component';
import { SharedModule } from './shared/shared.module';
import { LedgerComponent } from './ledger/ledger.component';
import { LedgerImportComponent } from './ledger/ledger-import/ledger-import.component';
import { AgGridModule } from 'ag-grid-angular';
import { LedgersListComponent } from './ledger/ledgers-list/ledgers-list.component';
import { AuthService } from './auth/service/auth.service';
import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';
import {
  ApiModule as IdentityServiceApi,
  BASE_PATH as ID_BASE_PATH,
} from 'ldt-identity-service-api';
import {
  ApiModule as LedgerServiceApiV2,
  BASE_PATH as LD_BASE_PATH_V2,
} from 'ldt-ledger-service-api-v2';
import {
  ApiModule as OnboardingServiceApi,
  BASE_PATH as OB_BASE_PATH,
} from 'ldt-onboarding-service-api';
import { ApiModule as CleanServiceApi, BASE_PATH as CL_BASE_PATH } from 'ldt-clean-service-api';
import { ApiModule as BillingServiceApi, BASE_PATH as BL_BASE_PATH } from 'ldt-billing-service-api';
import {
  ApiModule as DWReaderServiceApi,
  BASE_PATH as DWR_BASE_PATH,
} from 'ldt-dw-reader-service-api';
import {
  ApiModule as StateStorageServiceApi,
  BASE_PATH as SS_BASE_PATH,
} from 'ldt-state-storage-api';
import { ApiModule as ResolverApi, BASE_PATH as RES_BASE_PATH } from 'ldt-resolver-api';
import { ApiModule as ProvidersApi, BASE_PATH as PROVIDERS_BASE_PATH } from 'ldt-providers-api';
import { ApiModule as DevDeploysApi, BASE_PATH as DD_BASE_PATH } from 'ldt-dev-deploys-api';
import { ApiModule as MoneyballApi, BASE_PATH as MB_BASE_PATH } from 'ldt-moneyball-api';
import { ApiModule as PeopleApi, BASE_PATH as PP_BASE_PATH } from 'ldt-people-api';
import { ApiModule as DataDeliveryApi, BASE_PATH as DDE_BASE_PATH } from 'ldt-data-deliveries-api';
import { ApiModule as ChatApi, BASE_PATH as CHAT_BASE_PATH } from 'ldt-chat-api';
import { ApiModule as ChatProtoApi, BASE_PATH as CHAT_PROTO_BASE_PATH } from 'ldt-chat-proto-api';
import { ApiModule as ProviderIntakeApi, BASE_PATH as PI_BASE_PATH } from 'ldt-provider-intake-api';
import { ApiModule as TasksApi, BASE_PATH as TASKS_BASE_PATH } from 'ldt-tasks-api';
import { environment } from 'src/environments/environment';
import { AuthInterceptor } from './auth/auth-interceptor';
import { TeamComponent } from './team/team.component';
import { TeamUsersComponent } from './team/team-users/team-users.component';
import { TeamServiceAccountsComponent } from './team/team-service-accounts/team-service-accounts.component';
import { HeaderAcceptsInterceptor } from './api-client/header-response-type.interceptor';
import { HeaderAppVersionInterceptor } from './api-client/header-app-version';
import { TeamInvitationsComponent } from './team/team-invitations/team-invitations.component';
import { InvitationsCreateDialogComponent } from './team/invitations-create-dialog/invitations-create-dialog.component';
import { SaCreateDialogComponent } from './team/team-service-accounts/sa-create-dialog/sa-create-dialog.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FullMaterialModule } from './material/material-module';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import * as Sentry from '@sentry/angular';
import { SettingsBillingComponent } from './settings/billing//billing.component';
import { SettingsGeneralComponent } from './settings/general/general.component';
import { SettingsComponent } from './settings/settings.component';
import { IntegrationsComponent } from './integrations/integrations.component';
import { HighchartsChartModule } from 'highcharts-angular';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { SaNewSecretDialogComponent } from './team/team-service-accounts/sa-new-secret-dialog/sa-new-secret-dialog.component';
import { ClipboardModule } from 'ngx-clipboard';
import { HotkeyModule } from 'angular2-hotkeys';
import { FieldSelectorDialogComponent } from './shared/field-selector-dialog/field-selector-dialog.component';
import { GrowService, BASE_PATH as GS_BASE_PATH } from './grow/grow.service';
import { CreateLedgerDialogComponent } from './ledger/create-ledger-dialog/create-ledger-dialog.component';
import { LedgerContactsV2Component } from './ledger/ledger-contacts-v2/ledger-contacts-v2/ledger-contacts-v2.component';
import { LedgerDownloadDrawerComponent } from './ledger/ledger-download-drawer/ledger-download-drawer.component';
import { JobsTableCellRendererComponent } from './shared/jobs-table-cell-renderer/jobs-table-cell-renderer.component';
import { JobsTableComponent } from './shared/jobs-table/jobs-table.component';
import { LedgerOverviewV2Component } from './ledger/ledger-overview-v2/ledger-overview-v2.component';
import { PeopleDetailsComponent } from './people/details/details.component';
import { DWPeopleComponent } from './people/people.component';
import { PeopleFindComponent } from './people/find/find.component';
import { AvailableIntegrationsComponent } from './integrations/available-integrations/available-integrations.component';
import { ConfiguredIntegrationsComponent } from './integrations/configured-integrations/configured-integrations.component';
import { IntegrationDialogComponent } from './integrations/available-integrations/integration-dialog/integration-dialog.component';
import { FiltersComponent } from './people/filters/filters.component';
import { SimpleFilterComponent } from './people/filters/simple-filter/simple-filter.component';
import { FilterSubfiltersComponent } from './people/filters/filter-subfilters/filter-subfilters.component';
import { TestStatusDialogComponent } from './integrations/configured-integrations/test-status-dialog/test-status-dialog.component';
import { ConfigInfoComponent } from './integrations/helper-components/config-info/config-info.component';
import { DetailsTooltipCellComponent } from './integrations/helper-components/details-tooltip-cell/details-tooltip-cell.component';
import { FilterGroupComponent } from './people/filters/filter-group/filter-group.component';
import { FilterChipsComponent } from './people/filters/filter-chips/filter-chips.component';
import { FilterCompanySearchComponent } from './people/filters/filter-company-search/filter-company-search.component';
import { AddQuickBuildModalComponent } from './people/filters/add-quick-build-modal/add-quick-build-modal.component';
import { SubscriptionDialogComponent } from './settings/billing/subscription-dialog/subscription-dialog.component';
import { MoneyballProductSubscriptionComponent } from './settings/billing/products/moneyball/moneyball.component';
import { ChatProductSubscriptionComponent } from './settings/billing/products/chat/chat.component';
import { ChatFeedbackDialogComponent } from './people/filters/chat-feedback-dialog/chat-feedback-dialog.component';
import { ChatToFiltersComponent } from './people/filters/chat-to-filters/chat-to-filters.component';
import { PeopleChatComponent } from './shared/components/chat/chat.component';
import { NewChatComponent } from './shared/components/new-chat/new-chat.component';
import { ChatHistoryDrawerComponent } from './shared/components/chat-history-drawer/chat-history-drawer.component';
import { MarkdownModule } from 'ngx-markdown';
import { SpaceModule } from '@flatfile/angular-sdk';
import { FlatfileUploaderComponent } from './ledger/flatfile-uploader/flatfile-uploader.component';
import { ApiKeysComponent } from './team/api-keys/api-keys.component';
import { AkCreateDialogComponent } from './team/api-keys/ak-create-dialog/ak-create-dialog.component';
import { StreamsSubscribeDrawerComponent } from './shared/streams-subscribe-drawer/streams-subscribe-drawer.component';
import { StreamsSubscribeDrawerService } from './shared/streams-subscribe-drawer/streams-subscribe-drawer.service';
import { DownloadsComponent } from './downloads/downloads.component';
import { DownloadsListComponent } from './downloads/downloads-list/downloads-list.component';
import { DownloadDeliveryCellComponent } from './downloads/downloads-list/download-delivery-cell.component';
import { Log } from './logger.service';
import { HostnameService } from './hostname-router-service';
import { WorkforceInsightsComponent } from './workforce/insights/insights.component';
import { ChatWithInsightsComponent } from './workforce/chat-with-insights/chat-with-insights.component';
import { InsightsDashboardComponent } from './workforce/components/insights-dashboard/insights-dashboard.component';
import { SaveListModalComponent } from './workforce/save-list-modal/save-list-modal.component';
import { AccessLinksComponent } from './access-links/access-links.component';
import { PeopleProductSubscriptionComponent } from './settings/billing/products/people/people.component';
import { BootstrapComponent } from './bootstrap.component';
import { GotlivedataAppComponent } from './gotlivedata/app.gld.component';
import { WorkforceAppComponent } from './workforce/app.wf.component';
import { FindCompaniesComponent } from './people/find-companies/find-companies.component';
import { STATSIG_INIT_CONFIG } from '@statsig/angular-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { LedgerTrackedFieldsComponent } from './ledger/ledger-tracked-fields/ledger-tracked-fields.component';
import { WorkforcePeopleComponent } from './workforce/people/people.component';
import { WorkforceTeamComponent } from './workforce/team/team.component';
import { WorkforceApiKeysComponent } from './workforce/api-keys/api-keys.component';
import { BulkContentComponent } from './workforce/people/bulk-content/bulk-content.component';
import { BenchmarkDashboardComponent } from './workforce/components/benchmark-dashboard/benchmark-dashboard.component';
import { InDepthDashboardComponent } from './workforce/components/in-depth-dashboard/in-depth-dashboard.component';
import { HeadToHeadDashboardComponent } from './workforce/components/head-to-head-dashboard/head-to-head-dashboard.component';
import { LangGraphInstanceSelectorComponent } from './shared/components/langgraph-instance-selector/langgraph-instance-selector.component';
import { ChatSettingsDialogComponent } from './workforce/chat-with-insights/chat-settings-dialog/chat-settings-dialog.component';
import { NgxJsonTreeviewComponent } from 'ngx-json-treeview';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export function tokenGetter() {
  return localStorage.getItem('accessToken');
}

const StatsigConfig = {
  sdkKey: 'client-yZiu0UVnA5Qr2P2zOLGskpbRusFLUqSDjLPqkMGEMmI',
  user: {}, // initial user object
  options: { plugins: [new StatsigAutoCapturePlugin()] },
};
@NgModule({
  declarations: [
    GotlivedataAppComponent,
    MainPageComponent,
    AccountManagementComponent,
    LedgerComponent,
    LedgerImportComponent,
    LedgersListComponent,
    TeamComponent,
    TeamUsersComponent,
    TeamServiceAccountsComponent,
    TeamInvitationsComponent,
    InvitationsCreateDialogComponent,
    SaCreateDialogComponent,
    SettingsBillingComponent,
    SettingsGeneralComponent,
    SettingsComponent,
    IntegrationsComponent,
    SaNewSecretDialogComponent,
    CreateLedgerDialogComponent,
    LedgerContactsV2Component,
    LedgerDownloadDrawerComponent,
    JobsTableCellRendererComponent,
    LedgerOverviewV2Component,
    PeopleDetailsComponent,
    DWPeopleComponent,
    PeopleFindComponent,
    AvailableIntegrationsComponent,
    ConfiguredIntegrationsComponent,
    IntegrationDialogComponent,
    FiltersComponent,
    SimpleFilterComponent,
    FilterSubfiltersComponent,
    TestStatusDialogComponent,
    ConfigInfoComponent,
    DetailsTooltipCellComponent,
    FilterGroupComponent,
    FilterChipsComponent,
    FilterCompanySearchComponent,
    AddQuickBuildModalComponent,
    SubscriptionDialogComponent,
    MoneyballProductSubscriptionComponent,
    ChatProductSubscriptionComponent,
    PeopleProductSubscriptionComponent,
    ChatFeedbackDialogComponent,
    ChatToFiltersComponent,
    PeopleChatComponent,
    NewChatComponent,
    FlatfileUploaderComponent,
    ApiKeysComponent,
    AkCreateDialogComponent,
    StreamsSubscribeDrawerComponent,
    ChatHistoryDrawerComponent,
    WorkforceInsightsComponent,
    ChatWithInsightsComponent,
    InsightsDashboardComponent,
    SaveListModalComponent,
    DownloadsComponent,
    DownloadsListComponent,
    DownloadDeliveryCellComponent,
    AccessLinksComponent,
    BootstrapComponent,
    WorkforceAppComponent,
    FindCompaniesComponent,
    LedgerTrackedFieldsComponent,
    WorkforcePeopleComponent,
    WorkforceTeamComponent,
    WorkforceApiKeysComponent,
    OAuthConsentComponent,
    BenchmarkDashboardComponent,
    InDepthDashboardComponent,
    HeadToHeadDashboardComponent,
    LangGraphInstanceSelectorComponent,
    ChatSettingsDialogComponent,
  ],
  bootstrap: [BootstrapComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    FullMaterialModule,
    BrowserModule,
    RouterModule,
    AuthModule,
    AppRoutingModule,
    CommonModule,
    SharedModule,
    JobsTableComponent,
    FormsModule,
    ReactiveFormsModule,
    AgGridModule,
    IdentityServiceApi,
    LedgerServiceApiV2,
    CleanServiceApi,
    OnboardingServiceApi,
    BillingServiceApi,
    DWReaderServiceApi,
    DevDeploysApi,
    MoneyballApi,
    PeopleApi,
    DataDeliveryApi,
    ResolverApi,
    ChatApi,
    ProviderIntakeApi,
    ChatProtoApi,
    ProvidersApi,
    TasksApi,
    JwtModule.forRoot({
      config: {
        tokenGetter: tokenGetter,
        allowedDomains: [
          'dw.dwh-pipeline.staging.gotlivedata.io',
          'gotlivedata.io',
          'alternate.gotlivedata.io',
          'staging.gotlivedata.io',
          'localhost:5000',
          'dwh-pipeline.gotlivedata.io',
          'deliveries.dwh-pipeline.staging.gotlivedata.io',
          'resolver.dwh-pipeline.gotlivedata.io',
          'resolver.dwh-pipeline.staging.gotlivedata.io',
          'wkhlw1b9b1.execute-api.us-east-1.amazonaws.com',
          'dw-test.dwh-pipeline.gotlivedata.io',
        ],
        disallowedRoutes: [/.*\/session$/],
      },
    }),
    BrowserAnimationsModule,
    RecaptchaV3Module,
    HighchartsChartModule,
    NgxDaterangepickerMd.forRoot(),
    ClipboardModule,
    HotkeyModule.forRoot(),
    MarkdownModule.forRoot(),
    SpaceModule,
    HttpClientModule,
    BulkContentComponent,
    NgxJsonTreeviewComponent,
  ],
  providers: [
    AuthService,
    GrowService,
    HeapService,
    StreamsSubscribeDrawerService,
    HostnameService,
    Log,
    {
      provide: ID_BASE_PATH,
      useValue: environment.identityApiBasePath,
    },
    {
      provide: LD_BASE_PATH_V2,
      useValue: environment.ledgerApiBasePathV2,
    },
    {
      provide: GS_BASE_PATH,
      useValue: environment.growApiBasePath,
    },
    {
      provide: CL_BASE_PATH,
      useValue: environment.cleanApiBasePath,
    },
    {
      provide: OB_BASE_PATH,
      useValue: environment.onboardingApiBasePath,
    },
    {
      provide: BL_BASE_PATH,
      useValue: environment.billingApiBasePath,
    },
    {
      provide: DWR_BASE_PATH,
      useValue: environment.dwReaderApiBasePath,
    },
    {
      provide: DD_BASE_PATH,
      useValue: environment.devDeploysApiBasePath,
    },
    {
      provide: MB_BASE_PATH,
      useValue: environment.moneyballApiBasePath,
    },
    {
      provide: PP_BASE_PATH,
      useValue: environment.peopleApiBasePath,
    },
    {
      provide: DDE_BASE_PATH,
      useValue: environment.dataDeliveryApiBasePath,
    },
    {
      provide: RES_BASE_PATH,
      useValue: environment.resolverApiBasePath,
    },
    {
      provide: CHAT_BASE_PATH,
      useValue: environment.chatApiBasePath,
    },
    {
      provide: PI_BASE_PATH,
      useValue: environment.providerIntakeApiBasePath,
    },
    {
      provide: CHAT_PROTO_BASE_PATH,
      useValue: environment.chatProtoApiBasePath,
    },
    {
      provide: SS_BASE_PATH,
      useValue: environment.stateStorageApiBasePath,
    },
    {
      provide: PROVIDERS_BASE_PATH,
      useValue: environment.providersApiBasePath,
    },
    {
      provide: TASKS_BASE_PATH,
      useValue: environment.tasksApiBasePath,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeaderAcceptsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeaderAppVersionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaV3SiteKey,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
      useValue: undefined,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: STATSIG_INIT_CONFIG,
      useValue: StatsigConfig,
    },
    // provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
